import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { semanticColors } from 'src/constants/semanticTokens'
import { GSIcon } from 'src/components/shared/GSIcon'

interface Props {
  clientSecret: string
  onDismiss(): void
}

/**
 * The only time this secret is ever visible. The backend stores a sha256 hash, has no rotate
 * endpoint, no DELETE, and POST 400s once a client exists — so if it is lost, the customer's
 * integration cannot be re-credentialed by any admin action at all. Hence the warning palette
 * and an explicit Dismiss rather than an auto-hide.
 */
const OAuthClientSecretPanelComponent = ({ clientSecret, onDismiss }: Props) => {
  const snackbar = useSnackbar()

  const handleCopy = useCallback(async () => {
    if (!navigator.clipboard) {
      snackbar?.enqueueSnackbar('Unable to copy client secret', { variant: 'error' })

      return
    }

    try {
      await navigator.clipboard.writeText(clientSecret)
      snackbar?.enqueueSnackbar('Client secret copied', { variant: 'success' })
    } catch {
      snackbar?.enqueueSnackbar('Unable to copy client secret', { variant: 'error' })
    }
  }, [clientSecret, snackbar])

  return (
    <section
      data-testid="admin-customer-oauth-secret-panel"
      css={[
        tw`rounded-lg border border-solid p-6`,
        {
          borderColor: semanticColors.feedback.warningFg,
          backgroundColor: semanticColors.feedback.warningBg,
        },
      ]}
    >
      <Typography variant="h4" css={tw`mb-1 text-shades-900`}>
        Copy the client secret now
      </Typography>
      <Typography variant="body2" css={tw`mb-4 text-shades-700`}>
        This is the only time this secret will be shown. It cannot be retrieved or regenerated — if
        it is lost, this customer's integration cannot be re-credentialed.
      </Typography>
      <div css={tw`mb-4 flex flex-row items-center gap-2`}>
        <Typography
          variant="body2"
          css={tw`min-w-0 flex-1 break-all rounded-lg border border-solid border-shades-200 bg-shades-0 px-3 py-2 font-mono text-shades-1000`}
          data-testid="oauth-client-secret-value"
        >
          {clientSecret}
        </Typography>
        <Button
          size="small"
          color="white"
          iconOnly
          aria-label="Copy client secret"
          leftIcon={
            <GSIcon iconName="gs_square_behind_square_1" size={20} css={tw`text-shades-1000`} />
          }
          onClick={handleCopy}
          data-testid="oauth-copy-client-secret-button"
        />
      </div>
      <Button
        size="small"
        color="white"
        onClick={onDismiss}
        data-testid="oauth-dismiss-secret-button"
      >
        I've saved it — dismiss
      </Button>
    </section>
  )
}

export const OAuthClientSecretPanel = memo(OAuthClientSecretPanelComponent)
